import { ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { Alert, Button, rem, Text } from '@mantine/core';
import { SVGProps } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { getSelectionTier, TIER_THRESHOLDS } from '../../../utils/selectionManager';

const IconWarning = (props: SVGProps<SVGSVGElement>) => <ExclamationTriangleIcon {...props} />;
const IconBlock = (props: SVGProps<SVGSVGElement>) => <NoSymbolIcon {...props} />;

interface ExportLimitAlertProps {
    selectedCount: number;
    onReviewList?: () => void;
    mode?: 'selected' | 'all-found';
}

export const ExportLimitAlert = ({ selectedCount, onReviewList, mode = 'selected' }: ExportLimitAlertProps) => {
    const { t } = useTranslation();
    const tier = getSelectionTier(selectedCount);
    const noun = mode === 'all-found' ? t('EXPORT_ALERT_NOUN_FOUND') : t('EXPORT_ALERT_NOUN_SELECTED');

    if (tier === 'direct') {
        return null;
    }

    if (tier === 'warning') {
        return (
            <Alert
                color="yellow"
                title={t('EXPORT_ALERT_WARNING_TITLE', { count: selectedCount.toString(), noun })}
                icon={<IconWarning style={{ width: rem(18), height: rem(18) }} />}
                mb="xs"
            >
                <Text size="sm">
                    {t('EXPORT_ALERT_WARNING_TEXT')}
                </Text>
                {onReviewList && (
                    <Button
                        variant="filled"
                        color="orange"
                        size="compact-sm"
                        mt="xs"
                        onClick={onReviewList}
                        styles={{
                            root: {
                                backgroundColor: 'var(--mantine-color-orange-7)',
                                color: 'var(--mantine-color-white)',
                                border: '1px solid var(--mantine-color-orange-8)',
                            },
                        }}
                    >
                        {t('EXPORT_ALERT_REVIEW_LIST')}
                    </Button>
                )}
            </Alert>
        );
    }

    if (tier === 'mandatory-review') {
        return (
            <Alert
                color="orange"
                title={t('EXPORT_ALERT_MANDATORY_TITLE', { count: selectedCount.toString(), noun })}
                icon={<IconWarning style={{ width: rem(18), height: rem(18) }} />}
                mb="xs"
            >
                <Text size="sm">
                    {t('EXPORT_ALERT_MANDATORY_TEXT', {
                        min: (TIER_THRESHOLDS.WARNING + 1).toString(),
                        max: TIER_THRESHOLDS.MANDATORY_REVIEW.toString(),
                    })}
                </Text>
                {onReviewList && (
                    <Button
                        variant="filled"
                        color="orange"
                        size="compact-sm"
                        mt="xs"
                        onClick={onReviewList}
                        styles={{
                            root: {
                                backgroundColor: 'var(--mantine-color-orange-7)',
                                color: 'var(--mantine-color-white)',
                                border: '1px solid var(--mantine-color-orange-8)',
                            },
                        }}
                    >
                        {t('EXPORT_ALERT_OPEN_REVIEW')}
                    </Button>
                )}
            </Alert>
        );
    }

    if (tier === 'hard-block') {
        return (
            <Alert
                color="red"
                title={t('EXPORT_ALERT_BLOCK_TITLE', { noun, count: selectedCount.toString() })}
                icon={<IconBlock style={{ width: rem(18), height: rem(18) }} />}
                mb="xs"
            >
                <Text size="sm">
                    {t('EXPORT_ALERT_BLOCK_TEXT', { limit: TIER_THRESHOLDS.MANDATORY_REVIEW.toString() })}
                </Text>
            </Alert>
        );
    }

    return null;
};
